// src/components/Hero.js
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../LanguageContext.js';
import { ArrowRight } from 'lucide-react';

const Hero = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="home" 
      className="relative w-full min-h-screen flex items-center justify-start overflow-hidden pt-20"
    >
      {/* Background Image with parallax effect */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/DHOME_HERO.webp" 
          alt="" 
          className="w-full h-full object-cover"
        />
        {/* Sophisticated dark overlay with gradient */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.6) 100%)'
          }}
        ></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 w-full h-full flex items-center">
        <div className="max-w-2xl w-full">
          {/* Pre-title with animation */}
          <div 
            className={`mb-6 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '0.1s' }}
          >
            <span 
              className="text-xs font-medium uppercase tracking-widest"
              style={{ color: '#FFFFFF', opacity: 0.7 }}
            >
              {t('hero', 'hero_pretitle')}
            </span>
          </div>

          {/* Title with staggered animation */}
          <h1 
            className={`text-5xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight text-white mb-8 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.2s', fontFamily: 'Inter, sans-serif' }}
          >
            {t('hero', 'hero_title')}
          </h1>

          {/* Accent line */}
          <div 
            className={`w-20 h-1 mb-8 transition-all duration-700 ${isLoaded ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
            style={{ 
              backgroundColor: '#FFFFFF',
              transitionDelay: '0.3s',
              transformOrigin: 'left'
            }}
          ></div>

          {/* Subtitle with animation */}
          <p 
            className={`text-base md:text-xl leading-relaxed max-w-2xl transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ 
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 300,
              lineHeight: 1.7,
              transitionDelay: '0.4s'
            }}
          >
            {t('hero', 'hero_subtitle')}
          </p>

          {/* CTA Section with single button */}
          <div 
            className={`pt-10 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '0.5s' }}
          >
            {/* Primary CTA Button */}
            <button 
              onClick={() => scrollToSection('contact')}
              className="group px-8 py-3.5 font-semibold text-sm uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 hover:gap-3"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#000000'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F5F5F3';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              {t('hero', 'cta_button')}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator - more refined */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <p 
          className="text-xs uppercase tracking-widest"
          style={{ color: 'rgba(255,255,255,0.5)' }}
        >
          {t('hero', 'scroll_text')}
        </p>
        <div className="animate-bounce">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default Hero;